import type { TFunction } from 'i18next';
import { z } from 'zod';

export const requiredMessage = (t?: TFunction) =>
	t ? t('This field is required') : 'This field is required';

export type IsUnavailable = (key: string) => boolean;

export const relaxUnavailable = <S extends z.ZodRawShape>(
	shape: S,
	isUnavailable: IsUnavailable = () => false
): S =>
	Object.fromEntries(
		Object.entries(shape).map(([key, schema]) => [
			key,
			isUnavailable(key) ? z.unknown().optional() : schema
		])
	) as S;

export const trimmed = () =>
	z
		.string()
		.nullish()
		.transform(v => (v ?? '').trim());

export const collapsed = () =>
	z
		.string()
		.nullish()
		.transform(v => (v ?? '').replace(/\s+/g, ' ').trim());

export const requiredText = (t?: TFunction, { collapse = false } = {}) =>
	(collapse ? collapsed() : trimmed()).refine(v => v.length > 0, {
		message: requiredMessage(t)
	});

export const optionId = () =>
	z
		.string()
		.nullish()
		.transform(v => v ?? '');

export const requiredOptionId = (t?: TFunction) =>
	optionId().refine(v => v.length > 0, { message: requiredMessage(t) });

export const optionalBoolean = () => z.boolean().nullish();

export const yearValue = (t?: TFunction) => {
	const currentYear = new Date().getFullYear();
	const message = t
		? t('Year must be between 1900 and {{currentYear}}', { currentYear })
		: `Year must be between 1900 and ${currentYear}`;

	return z
		.number()
		.int()
		.refine(y => y >= 1900 && y <= currentYear, { message })
		.nullish();
};

export const maxLengthText = (max: number, t?: TFunction) =>
	trimmed().refine(v => v.length <= max, {
		message: t
			? t('Must be {{max}} characters or less', { max })
			: `Must be ${max} characters or less`
	});
